import { type FC, type ReactNode } from "react";
import { Button } from "antd";
import { CloseOutlined, TagOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { formatTranslation } from "../../../../shared/utils/translationUtils";
import { ReactComponent as SelectPlotsArrowIcon } from "../../../../assets/images/select-plots-arrow.svg";
import { ReactComponent as EditIcon } from "../../../../assets/images/product-card-edit.svg";
import "./style.less";

export interface ProductCardProps {
  productName: string;
  indicatorColor?: string;
  highlightColor?: string;
  variant?: "plots" | "stripes";
  tagLabel?: string;
  headingAction?: ReactNode;
  headerAction?: ReactNode;
  footer?: ReactNode;
  protocolText?: string;
  rangeText?: string;
  actionLabel?: string;
  actionIcon?: ReactNode;
  onRemoveTag?: () => void;
  onSelectPlots?: () => void;
  selectPlotsDisabled?: boolean;
  isSaved?: boolean;
  className?: string;
}

const ProductCard: FC<ProductCardProps> = ({
  productName,
  indicatorColor,
  highlightColor,
  variant = "plots",
  tagLabel,
  headingAction,
  headerAction,
  footer,
  protocolText,
  rangeText,
  actionLabel,
  actionIcon,
  onRemoveTag,
  onSelectPlots,
  selectPlotsDisabled = false,
  isSaved = false,
  className,
}) => {
  const { t } = useTranslation();
  const isStripes = variant === "stripes";

  return (
    <div
      className={`rd-product-card${
        isStripes ? " rd-product-card--stripes" : ""
      } ${className ?? ""}`}
      data-testid="product-card"
      style={
        highlightColor === undefined
          ? undefined
          : { borderColor: highlightColor, borderWidth: 2 }
      }
    >
      <div className="rd-product-card__body">
        <div className="rd-product-card__content">
          <div className="rd-product-card__header">
            <div className="rd-product-card__heading">
              <span
                className="rd-product-card__indicator"
                style={{ backgroundColor: indicatorColor }}
                aria-hidden="true"
              />
              <span
                className="rd-product-card__name"
                data-testid="product-card-name"
              >
                {productName}
              </span>

              {headingAction && (
                <span className="rd-product-card__heading-action">
                  {headingAction}
                </span>
              )}

              {isStripes && (
                <>
                  <span
                    className="rd-product-card__separator"
                    aria-hidden="true"
                  >
                    &bull;
                  </span>
                  <span className="rd-product-card__assignment-label">
                    {formatTranslation(
                      t("newRatesAndDosage.productCard.assignStripes")
                    )}
                  </span>
                </>
              )}
            </div>

            {tagLabel ? (
              <span
                className="rd-product-card__tag"
                data-testid="product-card-tag"
              >
                <TagOutlined className="rd-product-card__tag-icon" />
                <span className="rd-product-card__tag-label">{tagLabel}</span>
                <button
                  type="button"
                  className="rd-product-card__tag-remove"
                  aria-label={formatTranslation(
                    t("newRatesAndDosage.productCard.removeTag")
                  )}
                  onClick={onRemoveTag}
                >
                  <CloseOutlined />
                </button>
              </span>
            ) : (
              headerAction
            )}
          </div>

          <div className="rd-product-card__meta">
            {protocolText && (
              <span className="rd-product-card__meta-item">{protocolText}</span>
            )}
            {rangeText && (
              <span className="rd-product-card__meta-item rd-product-card__meta-item--muted">
                {rangeText}
              </span>
            )}
          </div>
        </div>
      </div>

      {footer ?? (
        <Button
          className="rd-product-card__select-btn"
          onClick={onSelectPlots}
          disabled={selectPlotsDisabled}
          data-testid="product-card-select-plots"
        >
          <span>
            {actionLabel ??
              formatTranslation(
                t(
                  isSaved
                    ? "trials.editAction"
                    : isStripes
                    ? "newRatesAndDosage.productCard.selectStrips"
                    : "newRatesAndDosage.productCard.selectPlots"
                )
              )}
          </span>

          {actionIcon ??
            (isSaved ? (
              <EditIcon aria-hidden="true" />
            ) : (
              <SelectPlotsArrowIcon
                aria-hidden="true"
                className="rd-product-card__select-btn-icon"
              />
            ))}
        </Button>
      )}
    </div>
  );
};

export default ProductCard;
